import axios, { AxiosResponse } from 'axios';
import { IUser } from '../../user';
import { ApiResponse, GetReportResponse, zps_ms_ea_war } from '../../zps';
import { handleDownload } from '../../../app/utils/script';

export type IPCTickets = {
  action?: number;
  cause?: number;
  fault?: number;
  problem?: number;
  seqn?: number;
};

export interface HeldeskClassificationItems {
  id: number;
  name: string;
}

export interface HeldeskClassificationResponse extends ApiResponse {
  actions: HeldeskClassificationItems[];
  causes: HeldeskClassificationItems[];
  faults: HeldeskClassificationItems[];
  problems: HeldeskClassificationItems[];
}
export interface HeldeskTicketsRequests {
  agent?: { entid: number; name: string };
  commChannel?: number;
  contact?: string;
  eid?: number;
  entid?: number;
  entityName?: string;
  intType?: number;
  issueType?: number;
  openDT?: string;
  pc?: IPCTickets[];
  sn?: string;
  status?: number;
  statusDT?: string;
  ticketID?: number;
  time?: number;
  urgency?: number;
}

export type ticketInfoLines = {
  classification?: number;
  comments?: string;
  description?: string;
  impact?: number;
  line?: number;
  origin?: string;
  soluClassif?: boolean;
  status?: number;
  statusDT?: string;
  ticketID?: number;
  time?: number;
  urgency?: number;
  user: IUser;
};

export type ticketInfoPc = {
  action: number;
  cause: number;
  fault: number;
  problem: number;
};

export interface HeldeskTicketInfoRequests {
  agent?: { entid: number; name: string };
  commChannel?: number;
  contact?: string;
  eid?: number;
  entid?: number;
  entityName?: string;
  intType?: number;
  issueType?: number;
  lines?: ticketInfoLines[];
  openDT?: string;
  pc?: ticketInfoPc[];
  product: string;
  sn?: string;
  status?: number;
  statusDT?: string;
  system?: string;
  ticketID: number;
  time?: number;
  urgency?: number;
  description?: string;
  comments?: string;
}

export interface HeldeskTicketInfoUpdateRequests {
  commChannel?: number;
  contact?: string;
  eid: number;
  entid: number;
  intType?: number;
  issueType?: number;
  openDT?: string;
  pc?: ticketInfoPc[];
  system?: null;
  ticketID?: number;
  description?: string;
  comments?: string;
}

export interface HeldeskTickeLineRequests {
  classification: number;
  comments: string;
  description: string;
  impact: number;
  line: number;
  origin: string;
  soluClassif: string;
  status: number;
  statusDT: string;
  ticketID: number;
  time: number;
  urgency: number;
  user: IUser;
}

export interface IHelpdeskTicketsResponse extends ApiResponse {
  tickets: HeldeskTicketsRequests[];
  totalRows: number;
  type: string;
}

export interface IHelpdeskTicketInfoResponse extends ApiResponse {
  tickets: HeldeskTicketInfoRequests[];
  totalRows: number;
  type: string;
}

export interface IHelpdeskTicketActionResponse extends ApiResponse {
  ticketId: number;
  type: string;
}
export const fetchHelpdeskTickets = (
  controller: AbortController,
  offset: number,
  limit: number,
  totalRows: boolean,
  ticketID?: number,
  eid?: number,
  entid?: number,
  closed?: string,
  bDT?: string | null,
  eDT?: string
): Promise<AxiosResponse<IHelpdeskTicketsResponse>> => {
  const url =
    `${zps_ms_ea_war}/ticket?offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    (ticketID ? `&ticketID=${ticketID}` : '') +
    (eid ? `&eid=${eid}` : '') +
    (entid ? `&entid=${entid}` : '') +
    (closed ? `&closed=${closed}` : '') +
    (bDT ? `&bDT=${bDT}` : '') +
    (eDT ? `&eDT=${eDT}` : '');

  return axios.get<IHelpdeskTicketsResponse>(url, { signal: controller.signal });
};

export const fetchHelpdeskTicketInfo = (
  ticketID: number,
  controller: AbortController
): Promise<AxiosResponse<IHelpdeskTicketInfoResponse>> => {
  return axios.get<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/${ticketID}`, { signal: controller.signal });
};

export const UpdateHelpdeskTicketInfo = (
  request: HeldeskTicketInfoUpdateRequests,
  controller: AbortController
): Promise<AxiosResponse<IHelpdeskTicketInfoResponse>> => {
  return axios.put<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/`, request, { signal: controller.signal });
};

export const fetchTicketClassifications = (
  controller: AbortController
): Promise<AxiosResponse<HeldeskClassificationResponse>> => {
  return axios.get<HeldeskClassificationResponse>(`${zps_ms_ea_war}/ticket/classification`, {
    signal: controller.signal
  });
};

export const addTicket = (
  request: HeldeskTicketInfoRequests,
  controller: AbortController
): Promise<AxiosResponse<IHelpdeskTicketActionResponse>> => {
  return axios.post(`${zps_ms_ea_war}/ticket/`, request, { signal: controller.signal });
};

export const updateTicket = (
  request: HeldeskTicketInfoUpdateRequests,
  controller: AbortController
): Promise<AxiosResponse<IHelpdeskTicketActionResponse>> => {
  return axios.put(`${zps_ms_ea_war}/ticket/`, request, { signal: controller.signal });
};

export const addTicketLine = (
  request: HeldeskTickeLineRequests,
  controller: AbortController
): Promise<AxiosResponse<IHelpdeskTicketInfoResponse>> => {
  return axios.post<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/line`, request, {
    signal: controller.signal
  });
};

export const updateTicketLine = (
  request: HeldeskTickeLineRequests,
  controller: AbortController
): Promise<AxiosResponse<IHelpdeskTicketInfoResponse>> => {
  return axios.put<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/line`, request, { signal: controller.signal });
};

export const fetchTicketsXLS = async (
  controller: AbortController,
  ticketID?: number,
  eid?: number,
  entid?: number,
  closed?: string,
  bDT?: string | null,
  eDT?: string
): Promise<AxiosResponse<GetReportResponse>> => {
  try {
    const res = await axios.get<GetReportResponse>(
      `${zps_ms_ea_war}/ticket/report?&format=xls` +
        (ticketID ? `&ticketID=${ticketID}` : '') +
        (eid ? `&eid=${eid}` : '') +
        (entid ? `&entid=${entid}` : '') +
        (closed ? `&closed=${closed}` : '') +
        (bDT ? `&bDT=${bDT}` : '') +
        (eDT ? `&eDT=${eDT}` : '')
    );

    if (res.data?.report) {
      handleDownload(res.data.report, res.data.name, res.data.mime);
    }

    return res;
  } catch (error) {
    throw error;
  }
};
